import React from 'react';

import { SharedHeaderProps } from '../../types/SharedHeaderProps';
import { SharedStepperProps } from '../../types/SharedStepperProps';
import { Theme } from '../../types/Theme';
import { Actions } from '../Actions/Actions';
import { Checkbox } from '../Checkbox/Checkbox';
import { Chips } from '../Chips/Chips';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Layout } from '../Layout/Layout';
import { Link } from '../Link/Link';
import { Stepper } from '../Stepper/Stepper';
import { TruncateText } from '../Text/subcomponents/TruncateText';

interface Props extends SharedHeaderProps {
  /** Supported DOM elements */
  as?: 'header' | 'summary';
  /** Enables padding */
  enablePadding?: boolean;
  /** Stepper contents */
  stepper?: SharedStepperProps;
  /** Optional theme. Defaults to 'light'. */
  theme?: Theme;
  /** Click handler */
  onClick?: (event?: MouseEvent) => void;
}

export const Header = ({
  as = 'header',
  actions,
  backAction,
  calloutContent,
  checkbox,
  chips,
  chipsPosition = 'right',
  enablePadding = true,
  icon,
  iconPosition = 'left',
  id,
  leadingAction,
  moreActions,
  moreActionsPosition = 'right',
  size,
  stepper,
  title,
  titleLink,
  toggleAction,
  onClick,
}: Props) => {
  const isDarkTheme = Boolean(stepper);

  const titleContent = (
    <TruncateText
      role="heading"
      variant="section"
      color={
        titleLink
          ? undefined
          : isDarkTheme
            ? 'inverse.text.primary'
            : 'text.primary'
      }
    >
      {title}
    </TruncateText>
  );

  const iconComponent = icon ? <Icon {...icon} /> : null;

  return (
    <Layout
      align="center"
      as={as}
      bg={isDarkTheme ? 'inverse.background' : undefined}
      borderBottom="border.divider"
      flex="none"
      id={id}
      justify="space-between"
      minH={size === 'l' ? 'header.height.l' : 'header.height'}
      pl={enablePadding ? 4 : 0}
      pr={enablePadding ? 3 : 0}
      py={1}
      role={as === 'summary' ? 'button' : undefined}
      spacing={2}
      onClick={onClick}
    >
      {stepper ? (
        <Stepper
          currentStepId={stepper.currentStepId}
          isOrdered={stepper.isOrdered}
          mode="header-nav"
          name="header-stepper"
          steps={stepper.steps}
        />
      ) : (
        <Layout align="center" minW={0} spacing={2}>
          {backAction && <IconButton {...backAction} icon="arrow-left" />}
          {toggleAction && <IconButton {...toggleAction} />}
          {leadingAction && <IconButton {...leadingAction} />}
          {checkbox && (
            <Checkbox
              name="header-checkbox"
              option={{ label: '', value: checkbox.checked }}
              value={checkbox.checked}
              labelMode="hidden"
              onChange={() => checkbox.onChange(!checkbox.checked)}
            />
          )}
          {iconPosition === 'left' && iconComponent}
          {chips && chipsPosition === 'left' && <Chips chips={chips} />}
          {titleLink ? (
            <Link {...titleLink} enableNewTab shouldTruncate variant="section">
              {titleContent}
            </Link>
          ) : (
            titleContent
          )}
          {iconPosition === 'right' && iconComponent}
          {chips && chipsPosition === 'right' && <Chips chips={chips} />}
        </Layout>
      )}
      <Layout align="center" spacing={2}>
        {calloutContent}
        {actions && (
          <Actions
            actions={actions}
            moreActions={moreActions}
            moreActionsPosition={moreActionsPosition}
          />
        )}
      </Layout>
    </Layout>
  );
};
